import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

export const useDecisions = ({
  chartId,
  sessionId,
  selectedEnc,
  getBaseUrl,
  findings,
  encounterTrace,
  setEncounterTrace,
  selectedFinding,
  setSelectedFinding,
}) => {

  // ── State ─────────────────────────────────────────────────────────────────
  const [decisions,        setDecisions]        = useState({});
  const [acceptedFindings, setAcceptedFindings]  = useState([]);
  const [historyCounts,    setHistoryCounts]     = useState({});
  const [decisionModal,    setDecisionModal]     = useState({ visible: false, action: null, finding: null });
  const [historyModal,     setHistoryModal]      = useState({ visible: false, history: [], loading: false, originalCode: null });

  // ── Persist to backend ────────────────────────────────────────────────────
  const persistDecision = useCallback(async (finding, decision, opts = {}) => {
    const { note = null, previousCode = null, newCode = null, newCodeDescription = null, modification = null } = opts;
    try {
      const base = await getBaseUrl();
      await fetch(`${base}/hcc/charts/${chartId}/decisions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionId}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          encounter_id:     selectedEnc?.encounter_id,
          icd10_code:       newCode || finding.icd10_code,              // ← use new code if provided
          code_description: newCodeDescription || finding.description,   // ← use new description if provided
          decision,
          source:           finding.source === 'coder' ? 'manually_added' : 'ai_proposed',
          finding_id:       finding.finding_id,
          evidence:         (finding.evidence || []).map(e => ({
            text: e.context?.match || e.entity_text || '', page: e.page, section: e.section,
          })),
          support:          (finding.corroboration?.supporting_evidence || []).map(s => ({
            text: s.text || s.context?.match || '', page: s.page, type: s.evidence_type || s.type || '', section: s.section,
          })),
          decision_note:    note,
          previous_code:    previousCode,
          new_code:         newCode,
          modification,
        }),
      });
    } catch (err) {
      console.error(`Failed to persist decision for ${finding.icd10_code}:`, err);
    }
  }, [chartId, sessionId, selectedEnc, getBaseUrl]);


  const setDecisionFor = useCallback((finding, decision, opts = {}) => {
      const { note = null, newCode = null, newDescription = null } = typeof opts === 'string' ? { note: opts } : opts;
      setDecisions(prev => ({
        ...prev,
        [finding.finding_id]: {
          decision,
          note,
          newCode,
          newDescription,
          originalCode: prev[finding.finding_id]?.originalCode || finding.icd10_code,
          originalDescription: prev[finding.finding_id]?.originalDescription || finding.description,
        },
      }));
      if (decision === 'accepted' || decision === 'modified') {
        setAcceptedFindings(prev => {
          if (prev.some(f => f.findingId === finding.finding_id)) return prev;
          return [...prev, {
            findingId:        finding.finding_id,
            icd10_code:       newCode || finding.icd10_code,
            description:      newDescription || finding.description,
            hcc_mapping:      finding.hcc_mapping,
            encounter_id:     selectedEnc?.encounter_id,
            encounter_number: selectedEnc?.encounter_number,
            encounter_date:   selectedEnc?.encounter_date || selectedEnc?.dos,
          }];
        });
      } else {
        setAcceptedFindings(prev => prev.filter(f => f.findingId !== finding.finding_id));
      }
    }, [selectedEnc]);

  // ── Action handlers ───────────────────────────────────────────────────────
  const handleAccept = useCallback((finding) => {
    if (!selectedEnc) return;
    setDecisionFor(finding, 'accepted');
    persistDecision(finding, 'accepted');
    setHistoryCounts(prev => ({
      ...prev,
      [finding.finding_id]: (prev[finding.finding_id] || 0) + 1,
    }));
  }, [selectedEnc, setDecisionFor, persistDecision]);



const handleModify = useCallback((finding) => {
    if (!selectedEnc) return;
    setDecisionModal({ visible: true, action: 'modified', finding });
  }, [selectedEnc]);

  const handleReject = useCallback((finding) => {
    if (!selectedEnc) return;
    setDecisionModal({ visible: true, action: 'rejected', finding });
  }, [selectedEnc]);

  const handleEscalate = useCallback((finding) => {
    if (!selectedEnc) return;
    setDecisionModal({ visible: true, action: 'under_review', finding });
  }, [selectedEnc]);

  const handleDecisionModalConfirm = useCallback((result) => {
      const { action, finding } = decisionModal;
      if (!finding || !action) return;
      const { note, newCode, newDescription } = result;

    if (action === 'modified' && newCode) {
      setDecisionFor(finding, 'modified', { note, newCode, newDescription });
      persistDecision(finding, 'modified', {
        note,
        previousCode: finding.icd10_code,
        newCode,
        newCodeDescription: newDescription,
        modification: {
          field: 'icd10_code',
          old_value: finding.icd10_code,
          new_value: newCode,
          old_description: finding.description,
          new_description: newDescription,
        },
      });
      setHistoryCounts(prev => ({
        ...prev,
        [finding.finding_id]: (prev[finding.finding_id] || 0) + 1,
      }));
      message.info(`${finding.icd10_code} → ${newCode}`);
      } else {
        // Reject / Review — standard flow
        setDecisionFor(finding, action, { note });
        persistDecision(finding, action, { note });
        const labels = { rejected: 'rejected', under_review: 'sent for review' };
        message.info(`${finding.icd10_code} ${labels[action] || action}`);
      }
      setDecisionModal({ visible: false, action: null, finding: null });
    }, [decisionModal, setDecisionFor, persistDecision, getBaseUrl, chartId, sessionId, selectedEnc]);

  const handleRevertDecision = useCallback((finding) => {
    if (!selectedEnc) return;
    const prevDecision = decisions[finding.finding_id]?.decision;
    // Use appropriate undo action name
    const undoAction = prevDecision === 'modified' ? 'reset'
                     : prevDecision === 'under_review' ? 'recalled'
                     : 'reverted';
    setDecisionFor(finding, 'pending');
    persistDecision(finding, undoAction);

    setHistoryCounts(prev => ({
      ...prev,
      [finding.finding_id]: (prev[finding.finding_id] || 0) + 1,
    }));    
    message.info(`${finding.icd10_code} reverted to pending`);
  }, [selectedEnc, decisions, setDecisionFor, persistDecision]);

  // Wrapper for CodesAcceptedPopup which passes findingId string
  const handleRevert = useCallback((findingId) => {
    const finding = findings.find(f => f.finding_id === findingId);
    if (finding) {
      handleRevertDecision(finding);
    } else {
      // Finding not in current encounter — just update local state
      setDecisionFor({ finding_id: findingId }, 'pending');
    }
  }, [findings, handleRevertDecision, setDecisionFor]);

  const fetchDecisionHistory = useCallback(async (finding) => {
    if (!finding?.finding_id || !selectedEnc) return;
    setHistoryModal({ visible: true, history: [], loading: true, originalCode: finding.icd10_code });
    try {
      const base = await getBaseUrl();
      const resp = await fetch(
        `${base}/hcc/charts/${chartId}/decisions/${selectedEnc.encounter_id}/${finding.finding_id}/history`,
        { headers: { 'Authorization': `Bearer ${sessionId}` }, credentials: 'include' }
      );
      if (resp.ok) {
        const data = await resp.json();
        setHistoryModal(prev => ({ ...prev, history: [...(data.history || [])].reverse(), loading: false }));
      } else {
        setHistoryModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setHistoryModal(prev => ({ ...prev, loading: false }));
    }
  }, [chartId, sessionId, selectedEnc, getBaseUrl]);

  // ── Load existing decisions on mount ──────────────────────────────────────
  // ── Load existing decisions on mount ──────────────────────────────────────
  useEffect(() => {
    if (!chartId) return;
    getBaseUrl().then(base =>
      fetch(`${base}/hcc/charts/${chartId}/decisions`, {
        headers: { 'Authorization': `Bearer ${sessionId}` },
        credentials: 'include',
      })
    ).then(r => r.ok ? r.json() : Promise.reject(r.status))
    .then(data => {
          const decisionsMap = {};
          const accepted = [];
          (data.decisions || []).forEach(d => {
            decisionsMap[d.finding_id] = {
              decision:            d.decision,
              note:                d.decision_note || null,
              newCode:             d.decision === 'modified' ? d.icd10_code : null,
              newDescription:      d.decision === 'modified' ? d.code_description : null,
              originalCode:        d.original_icd10_code || d.icd10_code,
              originalDescription: d.decision === 'modified' ? (d.old_code_description || null) : d.code_description,
            };
            if (d.decision === 'accepted' || d.decision === 'modified') {
              accepted.push({
                findingId:    d.finding_id,
                icd10_code:   d.icd10_code,
                description:  d.code_description,
                encounter_id: d.encounter_id,
              });
            }
          });
          setDecisions(decisionsMap);
          setHistoryCounts(data.history_counts || {});
          if (accepted.length > 0) setAcceptedFindings(accepted);
        })

    .catch(err => console.warn('Could not load saved decisions:', err));
  }, [chartId, sessionId]); // eslint-disable-line


  return {
    decisions,
    setDecisions,
    acceptedFindings,
    setAcceptedFindings,
    historyCounts,
    setHistoryCounts,
    decisionModal,
    historyModal,
    setDecisionModal,
    setHistoryModal,
    setDecisionFor,
    persistDecision,
    handleAccept,
    handleModify,
    handleReject,
    handleEscalate,
    handleDecisionModalConfirm,
    handleRevertDecision,
    handleRevert,
    fetchDecisionHistory,
  };
};